import * as React from 'react';
import * as _ from 'lodash';
import * as classNames from 'classnames';
import * as dateFns from 'date-fns';
import * as styles from './styles.scss';
import { Fallback } from 'components/common';
import {
  OrderFormViewMode,
  OrderKind,
  OrderType,
  SellBuyType,
  SymbolType,
  SystemType,
} from 'constants/enum';
import { Big } from 'big.js';
import {
  BrokerFunction,
  IAccount,
  IBrokerPhoneCode,
  IFormProps,
} from 'interfaces/common';
import {
  DerivativesConditionalType,
  IOrderForm,
  IOrderFormConfig,
  OrderFormFieldKey,
} from './config';
import { Form } from 'formik';
import { IState } from 'redux/global-reducers';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { queryOrderStockInfo } from './actions';
import { connect } from 'react-redux';
import { debounce } from 'utils/delay';
import { doBrokerVerifyPin } from 'redux/global-actions/Broker';
import { formatDateToString, formatStringToDate } from 'utils/datetime';
import { getAuthorizedBroker, handleError } from 'utils/common';
import { getAssetInforDerivatives } from 'components/areas/Account/AssetInformation/AssetInforDerivatives/actions';
import { getAssetInformation } from 'components/areas/Account/AssetInformation/AssetInforEquity/actions';
import { getMinLot, roundLot, roundStep } from 'utils/market';
import { openOtpModal } from 'redux/global-actions';
import { queryEquityConfirmDebt } from 'components/areas/Account/LoadDetail/ConfirmDebt/action';
import { withErrorBoundary } from 'react-error-boundary';
import ScrollBar from '../ScrollBar';
import ToggleSwitch from '../ToggleSwitch';
import addDays from 'date-fns/addDays';
import AccountSection from './FormSection/Account';
import SymbolSection from './FormSection/Symbol';
import QuantitySection from './FormSection/Quantity';
import ConditionalTypeSection from './FormSection/ConditionalType';
import PriceSection from './FormSection/Price';
import StopDateSection from './FormSection/StopDate';
import ExpiryDateSection from './FormSection/ExpiryDate';
import TradingValueSection from './FormSection/TradingValue';
import LimitPriceSection from './FormSection/LimitPrice';
import StopPriceSection from './FormSection/StopPrice';
import StopTimeSection from './FormSection/StopTime';
import OrderTypeSection from './FormSection/OrderType';
import BuySellSection from './FormSection/BuySellSection';
import ConfirmSection from './FormSection/ConfirmSection';
import ConfirmDebtModal from './FormSection/Modal/ConfirmDebtModal';
import ConfirmOrderModal from './FormSection/Modal/ConfirmOrderModal';
interface IOrderFormProps
  extends React.ClassAttributes<OrderFormComponent>,
    WithNamespaces,
    IFormProps<IOrderForm> {
  readonly currentSymbol: IState['currentSymbol'];
  readonly currentSymbolData: IState['currentSymbolData'];
  readonly selectedAccount: IState['selectedAccount'];
  readonly lang: IState['lang'];
  readonly orderStockInfo: IState['orderStockInfo'];
  readonly otpToken: IState['otpToken'];
  readonly symbolList: IState['symbolList'];
  readonly placeOrderResult: IState['placeOrderResult'];
  readonly config: IOrderFormConfig;
  readonly disabled?: boolean;
  readonly orderKind: OrderKind;
  readonly viewMode?: OrderFormViewMode;
  readonly configHyperLink: IState['config'];
  readonly assetInformation: IState['assetInformation'];
  readonly assetInforDerivatives: IState['assetInforDerivatives'];
  readonly equityConfirmDebt: IState['equityConfirmDebt'];
  readonly brokerVerifyPin: IState['brokerVerifyPin'];
  readonly equityPortfolio: IState['equityPortfolio'];
  readonly phoneCode?: string;
  readonly phoneCodeError?: boolean;

  readonly onSymbolSearch?: (code: string | null) => void;
  readonly openOtpModal: typeof openOtpModal;
  readonly queryOrderStockInfo: typeof queryOrderStockInfo;
  readonly getAssetInformation: typeof getAssetInformation;
  readonly getAssetInforDerivatives: typeof getAssetInforDerivatives;
  readonly queryEquityConfirmDebt: typeof queryEquityConfirmDebt;
  readonly doBrokerVerifyPin: typeof doBrokerVerifyPin;
  readonly onPhoneCodeBlur?: () => void;
  readonly onPhoneCodeChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface IOrderFormStates {
  readonly isTableMarginOpen?: boolean;
  readonly isTableCashOpen?: boolean;
  readonly isConfirmDebt?: boolean;
}

class OrderFormComponent extends React.Component<
  IOrderFormProps,
  IOrderFormStates
> {
  private containerRef: React.RefObject<HTMLDivElement>;
  private localBrokerVerifyPin: IBrokerPhoneCode | null = getAuthorizedBroker(
    this.props.brokerVerifyPin,
    this.props.selectedAccount,
    BrokerFunction.PLACE_ORDER_NORMAL
  );

  constructor(props: IOrderFormProps) {
    super(props);

    this.state = {};
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    const { selectedAccount } = this.props;
    const isBrokerCall = selectedAccount?.isBrokerCall;
    const isDerivatives = selectedAccount?.type === SystemType.DERIVATIVES;
    const isAuthorized = this.localBrokerVerifyPin?.isAuthorized;
    const accountNo = selectedAccount?.accountNumber ?? '';
    const clientID = selectedAccount?.clientID;
    const pin = this.props.phoneCode;
    if (isBrokerCall && isAuthorized) {
      isDerivatives
        ? this.props.getAssetInforDerivatives({ accountNo, clientID, pin })
        : this.props.getAssetInformation({
            accountNumber: accountNo,
            clientID,
          });
    } else {
      isDerivatives
        ? this.props.getAssetInforDerivatives({ accountNo })
        : this.props.getAssetInformation({
            accountNumber: accountNo,
          });
    }
  }

  componentDidUpdate(prevProps: IOrderFormProps) {
    if (
      (this.props.brokerVerifyPin !== prevProps.brokerVerifyPin ||
        this.props.selectedAccount !== prevProps.selectedAccount) &&
      this.props.selectedAccount != null
    ) {
      this.localBrokerVerifyPin = getAuthorizedBroker(
        this.props.brokerVerifyPin,
        this.props.selectedAccount,
        BrokerFunction.PLACE_ORDER_NORMAL
      );
      if (this.localBrokerVerifyPin?.isAuthorized) {
        if (this.props.selectedAccount.isDerivatives) {
          this.props.getAssetInforDerivatives({
            accountNo: this.props.selectedAccount.accountNumber,
            clientID: this.props.selectedAccount.clientID,
            pin: this.props.phoneCode,
          });
        } else {
          this.props.getAssetInformation({
            accountNumber: this.props.selectedAccount.accountNumber,
            clientID: this.props.selectedAccount.clientID,
          });
        }
      }

      if (!this.props.selectedAccount.isBrokerCall) {
        if (this.props.selectedAccount.isDerivatives) {
          this.props.getAssetInforDerivatives({
            accountNo: this.props.selectedAccount.accountNumber,
          });
        } else {
          this.props.getAssetInformation({
            accountNumber: this.props.selectedAccount.accountNumber,
            clientID: this.props.selectedAccount.clientID,
          });
        }
      }

      this.props.setFieldValue(
        'accountNumber',
        this.props.selectedAccount?.accountNumber
      );
    }

    if (prevProps.equityPortfolio !== this.props.equityPortfolio) {
      this.queryOrderStockInfo();
    }
    if (prevProps.orderStockInfo !== this.props.orderStockInfo) {
      const { values } = this.props;
      const maxQty = this.props.orderStockInfo.data?.maxQty;
      const orderQuantityPercentage = maxQty
        ? +Big(values.orderQuantity).div(maxQty).times(100).toFixed(1)
        : 0;
      const orderQuantity =
        values.orderType === OrderType.ODDLOT ||
        values.symbolType === SymbolType.BOND
          ? values.orderQuantity
          : roundLot(
              values.orderQuantity,
              values.market,
              values.symbolType,
              undefined,
              true
            );
      const tradingValue =
        values.orderPrice && orderQuantity
          ? +Big(values.orderPrice).times(orderQuantity)
          : 0;
      this.setValues({ maxQty, orderQuantityPercentage, tradingValue });
    }

    if (
      (prevProps.placeOrderResult !== this.props.placeOrderResult &&
        this.props.placeOrderResult.status.isSucceeded) ||
      (prevProps.otpToken === null &&
        prevProps.otpToken !== this.props.otpToken)
    ) {
      this.setValues({ isConfirmModalOpened: false });
    }
    if (
      prevProps.placeOrderResult !== this.props.placeOrderResult &&
      this.props.placeOrderResult.status.errorMessage === this.props.t('HKSRISK0016M')
    ) {
      this.setValues({ isConfirmModalOpened: false });
      this.setState({ isConfirmDebt: true });
    }

    if (
      prevProps.currentSymbolData !== this.props.currentSymbolData &&
      this.props.currentSymbolData?.s === this.props.values.stockCode &&
      this.props.currentSymbolData.c !== this.props.values.currentPrice
    ) {
      this.props.setFieldValue('currentPrice', this.props.currentSymbolData.c);
    }

    if (
      prevProps.selectedAccount !== this.props.selectedAccount ||
      prevProps.values !== this.props.values
    ) {
      this.props.setFieldValue(
        'accountNumber',
        this.props.selectedAccount?.accountNumber
      );
    }

    return true;
  }

  render() {
    const {
      t,
      values,
      config,
      currentSymbol,
      errors,
      selectedAccount,
      orderStockInfo,
      symbolList,
      lang,
      assetInforDerivatives,
      orderKind,
      placeOrderResult,
      isValid,
      viewMode,
    } = this.props;
    const symbol =
      currentSymbol.code === values.stockCode
        ? currentSymbol.infoData
        : symbolList.map?.[values.stockCode];
    const isDerivatives = selectedAccount?.type === SystemType.DERIVATIVES;
    const isOddlot =
      values.orderType === OrderType.ODDLOT ||
      symbol?.t === SymbolType.BOND ||
      values.orderType === OrderType.BUY_IN;
    const purchasingPower = isDerivatives
      ? (assetInforDerivatives.data?.cashInformation.internal.EE ?? 0) <= 0
        ? 0
        : assetInforDerivatives.data?.cashInformation.exchange.EE
      : orderStockInfo.data?.PP;
    const marginRatio = orderStockInfo.data?.marginRatio;
    const marginPriceCap = orderStockInfo.data?.marginPriceCap;
    const quantityStep =
      !isDerivatives && values.orderPrice
        ? +Big(values.orderPrice).times(
            getMinLot(symbol?.m, symbol?.t, isOddlot)
          )
        : 1000;
    const currentSymbolDesc =
      symbol != null ? <span>{values.stockCode}</span> : values.stockCode;
    const maxQty =
      values.maxQty && values.maxQty > 500000 ? 500000 : values.maxQty;

    const isSubmitDisabled =
      !isValid ||
      orderStockInfo.status.isLoading ||
      placeOrderResult.status.isLoading;
    const isStopLimitOrder = orderKind === OrderKind.STOP_LIMIT_ORDER;
    const isConditionalOrder = orderKind === OrderKind.CONDITIONAL_ORDER;
    const isNormalOrder = orderKind === OrderKind.NORMAL_ORDER;
    const isHorizontal = viewMode === OrderFormViewMode.HORIZONTAL;
    const sellType = values.sellBuyType === SellBuyType.SELL;
    const isNotOrderTypeLO = values.orderType !== OrderType.LO;
    const buyType = values.sellBuyType === SellBuyType.BUY;
    return (
      <>
        <ScrollBar autoHide={true}>
          <Form
            className={classNames(styles.OrderForm, {
              [styles.HorizontalForm]: isHorizontal,
              [styles.NormalOrder]: isNormalOrder,
            })}
          >
            {/* BUYSELL_SECTION_GLOBAL*/}
            <BuySellSection
              config={config}
              containerRef={this.containerRef}
              buyType={buyType}
              sellType={sellType}
              onChangeBuyType={this.onChangeBuyType}
              onChangeSellType={this.onChangeSellType}
            />

            {/* SYMBOLSECTION_GLOBAL */}
            <SymbolSection
              config={config}
              isHorizontal={isHorizontal}
              onClickPrice={this.onClickPrice}
              marginRatio={marginRatio}
              marginPriceCap={marginPriceCap}
              ErrorMessage={ErrorMessage}
              errors={errors}
              currentSymbolDesc={currentSymbolDesc}
              symbol={symbol}
              lang={lang}
              isDerivatives={isDerivatives}
              onSymbolSearch={this.onSymbolSearch}
            />

            {/* ACCOUNTSECTION_GLOABAL*/}
            <AccountSection
              config={config}
              isHorizontal={isHorizontal}
              onChangeAccount={this.onChangeAccount}
              onChangeQuantity={this.onChangeQuantity}
              onChangeOrderType={this.onChangeOrderType}
              buyType={buyType}
              purchasingPower={purchasingPower}
              values={values}
              sellType={sellType}
              onClickPrice={this.onClickPrice}
              marginRatio={marginRatio}
              marginPriceCap={marginPriceCap}
              ErrorMessage={ErrorMessage}
              errors={errors}
            />

            {/* PHONE_CODE_BROKER */}
            {selectedAccount?.isBrokerCall &&
              !this.localBrokerVerifyPin?.isAuthorized && (
                <div
                  className={styles.PhoneCode}
                  style={
                    isHorizontal
                      ? { order: 2, gridColumn: '2/3' }
                      : { order: 0, gridColumn: '1/7' }
                  }
                >
                  <div>
                    <span>{t('Phone Code')}</span>
                    <form action="" onSubmit={this.handleSubmitPhoneCode}>
                      <input
                        type="text"
                        onChange={this.props.onPhoneCodeChange}
                        onBlur={this.props.onPhoneCodeBlur}
                      />
                      <input type="submit" hidden />
                    </form>
                  </div>
                  <div>
                    <div className={styles.Row}>
                      {this.props.phoneCodeError && (
                        <div className={styles.ErrorPhoneCode}>
                          {this.props.t('PHONECODE_SHOULD_BE_NUMBER')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* QUANTITY_SECTION */}
            <QuantitySection
              config={config}
              values={values}
              ErrorMessage={ErrorMessage}
              errors={errors}
              isDerivatives={isDerivatives}
              symbol={symbol}
              onChangeQuantity={this.onChangeQuantity}
              onBlurQuantity={this.onBlurQuantity}
              isStopLimitOrder={isStopLimitOrder}
              isConditionalOrder={isConditionalOrder}
              maxQty={maxQty}
              isOddlot={isOddlot}
            />

            {/* CONDITIONAL_TYPE_SECTION */}
            <ConditionalTypeSection
              config={config}
              values={values}
              ErrorMessage={ErrorMessage}
              errors={errors}
              onChangeConditionType={this.onChangeConditionType}
            />

            {/* PRICE */}
            {(isConditionalOrder
              ? values.conditionalType !== DerivativesConditionalType.STOP_LIMIT
              : config[OrderFormFieldKey.PRICE] != null) && (
              <PriceSection
                config={config}
                values={values}
                ErrorMessage={ErrorMessage}
                errors={errors}
                symbol={symbol}
                onChangePrice={this.onChangePrice}
                onBlurPrice={this.onBlurPrice}
                onChangeOrderType={this.onChangeOrderType}
                orderKind={orderKind}
                isHorizontal={isHorizontal}
              />
            )}

            {/* ORDERTYPE */}
            {isNormalOrder && (
              <OrderTypeSection
                config={config}
                values={values}
                ErrorMessage={ErrorMessage}
                errors={errors}
                isHorizontal={isHorizontal}
                symbol={symbol}
                onBlurPrice={this.onBlurPrice}
                onChangeOrderType={this.onChangeOrderType}
                orderKind={orderKind}
              />
            )}

            {/* STOP_TIME */}
            {config[OrderFormFieldKey.STOP_TIME] != null &&
              values.conditionalType === DerivativesConditionalType.TIME && (
                <StopTimeSection
                  config={config}
                  values={values}
                  ErrorMessage={ErrorMessage}
                  errors={errors}
                  onChangeStopTime={this.onChangeStopTime}
                />
              )}

            {/* STOP_PRICE */}
            {(isConditionalOrder
              ? values.conditionalType === DerivativesConditionalType.STOP_LIMIT
              : config[OrderFormFieldKey.STOP_PRICE] != null) && (
              <StopPriceSection
                config={config}
                values={values}
                ErrorMessage={ErrorMessage}
                errors={errors}
                onChangeStopPrice={this.onChangeStopPrice}
                onBlurStopPrice={this.onBlurStopPrice}
                symbol={symbol}
                orderKind={orderKind}
              />
            )}

            {/* LIMIT_PRICE */}
            {(isConditionalOrder
              ? values.conditionalType === DerivativesConditionalType.STOP_LIMIT
              : config[OrderFormFieldKey.LIMIT_PRICE] != null) && (
              <LimitPriceSection
                config={config}
                values={values}
                ErrorMessage={ErrorMessage}
                errors={errors}
                onChangePrice={this.onChangePrice}
                onBlurPrice={this.onBlurPrice}
                onChangeOrderType={this.onChangeOrderType}
                symbol={symbol}
                orderKind={orderKind}
              />
            )}

            {/* TRADING_VALUE */}
            <TradingValueSection
              config={config}
              values={values}
              onChangeTradingValue={this.onChangeTradingValue}
              quantityStep={quantityStep}
              onBlurTradingValue={this.onBlurTradingValue}
              ErrorMessage={ErrorMessage}
              errors={errors}
              isDerivatives={isDerivatives}
            />

            {/* EXPIRY_DATE */}
            <ExpiryDateSection
              config={config}
              values={values}
              ErrorMessage={ErrorMessage}
              errors={errors}
              isDerivatives={isDerivatives}
              isOddlot={isOddlot}
              isNotOrderTypeLO={isNotOrderTypeLO}
              onChangeExpiryDate={this.onChangeExpiryDate}
              onChangeExpiryCheckBox={this.onChangeExpiryCheckBox}
              isWeekday={this.isWeekday}
            />

            {/* STOP_DATE */}
            {(isConditionalOrder
              ? values.conditionalType === DerivativesConditionalType.STOP_LIMIT
              : config[OrderFormFieldKey.STOP_DATE] != null) && (
              <StopDateSection
                config={config}
                onChangeStartDate={this.onChangeStartDate}
                onChangeEndDate={this.onChangeEndDate}
                values={values}
                isHorizontal={isHorizontal}
                ErrorMessage={ErrorMessage}
                errors={errors}
              />
            )}

            {/* SKIP_CONFIRMATION */}
            <div
              className={classNames(styles.SkipSection, styles.FormField, {
                [styles.DerivativesField]: isDerivatives,
              })}
              style={config[OrderFormFieldKey.SKIP_CONFIRMATION]?.style}
            >
              <div className={styles.MainField}>
                <div
                  className={classNames(styles.FieldInput, styles.MarginTop16)}
                >
                  <ToggleSwitch
                    label={'Skip Confirmation'}
                    onChange={this.onChangeConfirmation}
                    checked={values.skipConfirmation}
                  />
                </div>
              </div>
            </div>
          </Form>
        </ScrollBar>

        {/* CONFIRM_SECTION_BTN */}
        <ConfirmSection
          isDerivatives={isDerivatives}
          isHorizontal={isHorizontal}
          onOpenModal={this.onOpenModal}
          isSubmitDisabled={isSubmitDisabled}
          symbol={symbol}
          sellBuyType={values.sellBuyType}
          buyType={buyType}
          sellType={sellType}
        />

        {/* OTP_Confirm_Modal */}
        <ConfirmOrderModal
          values={values}
          onCloseModal={this.onCloseModal}
          onSubmit={this.onSubmit}
          isSubmitDisabled={isSubmitDisabled}
          orderKind={orderKind}
          selectedAccount={selectedAccount}
          isStopLimitOrder={isStopLimitOrder}
          isConditionalOrder={isConditionalOrder}
          isDerivatives={isDerivatives}
        />

        {/* OTP_Confirm_Debt */}
        <ConfirmDebtModal
          isConfirmDebt={this.state.isConfirmDebt}
          onCloseModalConfirmDebt={this.onCloseModalConfirmDebt}
        />
      </>
    );
  }

  private isWeekday = (date: Date) => {
    const day = dateFns.getDay(date);
    return day !== 0 && day !== 6;
  };

  private handleSubmitPhoneCode = () => {
    if (
      this.props.phoneCode &&
      !this.props.phoneCodeError &&
      this.props.selectedAccount?.clientID
    ) {
      this.props.doBrokerVerifyPin({
        brokerFunction: BrokerFunction.PLACE_ORDER_NORMAL,
        pin: this.props.phoneCode,
        clientID: this.props.selectedAccount?.clientID,
      });
    }
  };


  private onCloseModalConfirmDebt = () => {
    this.setState({ isConfirmDebt: false });
  };

  private onChangeAccount = (account?: IAccount) => {
    this.props.setFieldValue('accountNumber', account?.accountNumber);
  };

  private onChangeBuyType = () => {
    this.queryOrderStockInfo(SellBuyType.BUY);
    this.setValues({
      sellBuyType: SellBuyType.BUY,
      orderType:
        this.props.values.orderType === OrderType.BUY_IN //type BUYIN not exist in BUY_TYPE
          ? OrderType.LO
          : this.props.values.orderType,
      orderQuantityType: 'share',
    });
  };

  private onChangeSellType = () => {
    this.queryOrderStockInfo(SellBuyType.SELL);
    this.setValues({
      sellBuyType: SellBuyType.SELL,
      orderQuantityType: 'share',
    });
  };

  private onClickPrice = (orderPrice?: number) => {
    const { values, orderKind } = this.props;
    const tradingValue =
      values.orderQuantity && orderPrice
        ? values.orderQuantity * orderPrice
        : 0;
    if (
      values.orderType === OrderType.LO ||
      values.orderType === OrderType.ODDLOT
    ) {
      const isSpecialOrderKind =
        orderKind === OrderKind.STOP_LIMIT_ORDER ||
        orderKind === OrderKind.CONDITIONAL_ORDER;
      this.setValues({
        orderPrice,
        tradingValue,
        ...(isSpecialOrderKind && { stopPrice: orderPrice }),
      });
      this.queryOrderStockInfo(undefined, orderPrice);
    }
  };

  private onChangeQuantity = (
    quantity: number | undefined,
    orderQuantityType: 'share' | 'amount' | 'percentage',
    orderQuantityPercentage?: number
  ) => {
    const { values } = this.props;
    const tradingValue =
      values.orderType === OrderType.BUY_IN
        ? (values.ceilingPrice ?? 0) * (quantity ?? 0)
        : (values.orderPrice ?? 0) * (quantity ?? 0);

    this.setValues({
      orderQuantity: quantity ?? 0,
      orderQuantityPercentage,
      orderQuantityType,
      tradingValue,
    });
  };

  private onBlurQuantity = (value: number | undefined) => {
    const { values } = this.props;
    const orderQuantity =
      values.symbolType === SymbolType.BOND ||
      values.orderType === OrderType.BUY_IN
        ? values.orderQuantity > 0
          ? values.orderQuantity
          : 0
        : values.orderType === OrderType.ODDLOT
        ? values.orderQuantity >= 100 || values.orderQuantity < 0
          ? 99
          : values.orderQuantity
        : roundLot(
            value ?? 0,
            values.market,
            values.symbolType,
            undefined,
            true
          );
    const tradingValue =
      values.orderType === OrderType.BUY_IN
        ? (values.ceilingPrice ?? 0) * orderQuantity
        : (values.orderPrice ?? 0) * orderQuantity;

    const orderQuantityPercentage = values.maxQty
      ? +Big(orderQuantity).div(values.maxQty).times(100).toFixed(2)
      : 0;
    this.setValues({
      orderQuantity,
      orderQuantityPercentage,
      tradingValue,
    });
  };

  private roundStepPrice = (priceStep: number) => {
    const { values } = this.props;
    const orderPrice = roundStep(values.orderPrice, priceStep);
    const tradingValue =
      values.orderQuantity && orderPrice && typeof orderPrice === 'number'
        ? values.orderQuantity * orderPrice
        : 0;
    this.setValues({ orderPrice, tradingValue });
  };

  private onBlurPrice = (priceStep?: number) => {
    const { values, selectedAccount } = this.props;
    if (
      (values.orderType === OrderType.LO ||
        values.orderType === OrderType.ODDLOT) &&
      selectedAccount?.type !== SystemType.DERIVATIVES
    ) {
      debounce(this.roundStepPrice, 0)(priceStep);
    }
  };

  private onChangePrice = (orderPrice?: number) => {
    const { values } = this.props;
    const tradingValue =
      typeof orderPrice === 'number' ? values.orderQuantity * orderPrice : 0;

    this.setValues({
      orderPrice,
      tradingValue,
    });
    if (
      values.orderType === OrderType.LO ||
      values.orderType === OrderType.ODDLOT
    ) {
      debounce(this.queryOrderStockInfo, 1000)();
    }
  };

  private onChangeOrderType = (orderType: OrderType, orderQtt?: number) => {
    const { values } = this.props;
    const orderQuantity = !orderQtt
      ? orderType === OrderType.ODDLOT
        ? getMinLot(
            values.market,
            values.symbolType,
            orderType === OrderType.ODDLOT
          )
        : roundLot(
            values.orderQuantity,
            values.market,
            values.symbolType,
            undefined,
            true
          )
      : orderQtt;
    if (orderType !== OrderType.LO && orderType !== OrderType.ODDLOT) {
      this.setValues({
        orderType,
        orderQuantity,
        orderQuantityType: 'share',
        orderPrice: undefined,
        tradingValue:
          orderType === OrderType.BUY_IN
            ? orderQuantity * (values.ceilingPrice ?? 0)
            : undefined,
        expiryDate: undefined,
      });
    } else {
      const orderPrice = values.orderPrice ?? values.referencePrice;
      const tradingValue = orderPrice ? orderQuantity * orderPrice : 0;
      this.setValues({
        orderPrice,
        tradingValue,
        orderType,
        orderQuantity,
        ...(orderType === OrderType.ODDLOT && {
          orderQuantityType: 'share',
        }),
        ...(orderType !== OrderType.LO && {
          expiryDate: undefined,
        }),
      });
    }
  };

  private onChangeTradingValue = (tradingValue: number | undefined) => {
    const orderQuantity =
      tradingValue && this.props.values.orderPrice
        ? Math.round(tradingValue / this.props.values.orderPrice)
        : 0;
    this.setValues({ tradingValue, orderQuantity });
  };

  private onBlurTradingValue = () => {
    const orderQuantity = roundLot(
      this.props.values.orderQuantity,
      this.props.values.market,
      this.props.values.symbolType,
      undefined,
      true
    );

    const tradingValue =
      this.props.values.orderPrice != null
        ? +Big(orderQuantity).times(this.props.values.orderPrice)
        : 0;

    this.setValues({ tradingValue, orderQuantity });
  };

  private onChangeExpiryDate = (date: Date) => {
    this.setValues({ expiryDate: formatDateToString(date, 'yyyyMMdd') });
  };

  private onChangeStopTime = (date: Date) => {
    this.setValues({ stopTime: formatDateToString(date, 'HHmm') });
  };

  private onSymbolSearch = (stockCode: string | null) => {
    if (stockCode != null) {
      this.props.onSymbolSearch?.(stockCode);
      this.setValues({ stockCode });
    }
  };

  private roundStepStopPrice = (step?: number) => {
    const stopPrice = roundStep(this.props.values.stopPrice, step);
    this.setValues({ stopPrice });
  };

  private onBlurStopPrice = (priceStep?: number) => {
    this.props.selectedAccount?.type !== SystemType.DERIVATIVES &&
      debounce(this.roundStepStopPrice, 0)(priceStep);
  };

  private onChangeStopPrice = (stopPrice?: number) => {
    this.setValues({ stopPrice });
  };

  private onChangeStartDate = (date: Date) => {
    const { values } = this.props;
    const stringDate = formatDateToString(date);
    if (
      values.endDate != null &&
      values.startDate != null &&
      (formatStringToDate(values.endDate).getTime() < date.getTime() ||
        formatStringToDate(values.endDate).getTime() >
          addDays(date, 6).getTime())
    ) {
      this.setValues({ startDate: stringDate, endDate: stringDate });
    } else {
      this.setValues({ startDate: stringDate });
    }
  };

  private onChangeEndDate = (date: Date) => {
    this.setValues({ endDate: formatDateToString(date) });
  };

  private onChangeExpiryCheckBox = (value: boolean) => {
    this.setValues({
      expiryDate: value
        ? formatDateToString(dateFns.add(new Date(), { days: 1 }))
        : undefined,
    });
  };

  private onChangeConfirmation = (checked: boolean) => {
    this.setValues({ skipConfirmation: checked });
  };

  private onChangeConditionType = (title: string, value: string) => {
    this.setValues({ conditionalType: value as DerivativesConditionalType });
  };

  private onCloseModal = () => {
    this.setValues({ isConfirmModalOpened: false });
  };

  private onOpenModal = () => {
    const { values, selectedAccount, isValid, otpToken } = this.props;

    if (!isValid) {
      return;
    }

    if (values.skipConfirmation) {
      if (otpToken != null || selectedAccount?.isBrokerCall) {
        this.props.handleSubmit();
      } else {
        this.setValues({ isConfirmModalOpened: true });
      }
    } else {
      this.setValues({ isConfirmModalOpened: true });
    }
  };

  private onSubmit = () => {
    this.props.handleSubmit();
  };

  private setValues = (values: Partial<IOrderForm>) => {
    this.props.setValues({
      ...this.props.values,
      ...values,
    });
  };

  private queryOrderStockInfo = (
    sellBuyType?: SellBuyType,
    orderPrice?: number
  ) => {
    const { values, selectedAccount, orderStockInfo } = this.props;
    if (
      values.market != null &&
      values.referencePrice != null &&
      selectedAccount != null
    ) {
      let params = {
        accountNumber: values.accountNumber,
        accountType: selectedAccount.accountType,
        market: values.market,
        price:
          orderPrice ??
          values.orderPrice ??
          values.currentPrice ??
          values.referencePrice,
        sellBuyType: sellBuyType ?? values.sellBuyType,
        symbolCode: values.stockCode,
        onlyGetMaxQty: Boolean(orderStockInfo?.data?.PP),
      };
      params = selectedAccount?.isBrokerCall
        ? {
            ...params,
            ...{
              clientID: selectedAccount?.clientID,
              pin: this.localBrokerVerifyPin?.pin,
            },
          }
        : params;
      this.props.queryOrderStockInfo(params);
    }
  };
}

const mapStateToProps = (state: IState) => ({
  symbolList: state.symbolList,
  currentSymbol: state.currentSymbol,
  currentSymbolData: state.currentSymbolData,
  selectedAccount: state.selectedAccount,
  lang: state.lang,
  orderStockInfo: state.orderStockInfo,
  otpToken: state.otpToken,
  placeOrderResult: state.placeOrderResult,
  configHyperLink: state.config,
  assetInformation: state.assetInformation,
  assetInforDerivatives: state.assetInforDerivatives,
  equityConfirmDebt: state.equityConfirmDebt,
  brokerVerifyPin: state.brokerVerifyPin,
  equityPortfolio: state.equityPortfolio,
});

const mapDispatchToProps = {
  openOtpModal,
  queryOrderStockInfo,
  getAssetInformation,
  getAssetInforDerivatives,
  queryEquityConfirmDebt,
  doBrokerVerifyPin,
};

const OrderForm = withErrorBoundary(
  withNamespaces('common')(
    connect(mapStateToProps, mapDispatchToProps)(OrderFormComponent)
  ),
  Fallback,
  handleError
);

const ErrorMessage: React.FC<{ readonly message?: string }> = ({ message }) =>
  message ? (
    <span className={styles.FieldError} title={message}>
      {message}
    </span>
  ) : null;

export default OrderForm;



// WEBPACK FOOTER //
// ./src/components/common/OrderForm/form.tsx